import { useMemo, useState } from 'react'
import { Grid, Link, Pagination, Skeleton, Typography } from '@mui/material'

import AlertCard from '../../../../components/alert-card'
import UrgentAlertsBanner from '../../../../components/urgent-alerts-banner'
import useDates from '../../../../utils/hooks/useDates'
import { groupAlertsByDate } from '../../../alerts/helpers'
import classes from '../index.styles'

interface IProps {
  device: IDeviceType | null
}

const AlertsSection = ({ device }: IProps) => {
  const [activePage, setActivePage] = useState(1)
  const { formatCalendarDate, formatDateLongMonth } = useDates()

  const getAlertDateGroupLabel = (date: Date) => {
    const nearDateLabel = formatCalendarDate(date)
    if (nearDateLabel.startsWith('Today')) return 'Today'
    if (nearDateLabel.startsWith('Yesterday')) return 'Yesterday'
    return formatDateLongMonth(date)
  }

  const sortedAlerts = useMemo(() => {
    return [...(device?.alerts || [])].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [device?.alerts])

  const PAGE_SIZE = 5
  const groupedAlerts = groupAlertsByDate(sortedAlerts, activePage, PAGE_SIZE, getAlertDateGroupLabel)
  const currentGroupedAlerts = groupedAlerts
  const currentPage = activePage
  const setCurrentPage = setActivePage
  const activeAlerts = sortedAlerts.filter((alert) => alert.status === 'active')
  const activeUrgentAlerts = sortedAlerts.filter((alert) => alert.status === 'active' && alert.tags.includes('urgent') && alert.responseHistory.length === 0)

  return (
    <Grid container={true} size={12} sx={classes.sectionOuterGrid}>
      <Grid container={true} size={12} sx={classes.sectionTitleGrid}>
        <Typography variant="subtitle1" sx={classes.sectionTitle}>
          {device ? 'Alerts' : <Skeleton width={60} />}
        </Typography>
      </Grid>

      {activeUrgentAlerts.length > 0 && (
        <Grid container={true} size={12} sx={classes.urgentAlertsContainer}>
          {device ? (
            <Typography variant="body2">
              Please address these alerts ASAP in the{' '}
              <Link href="https://meshifyprotect.app.link/download-app" target="_blank" rel="noopener" sx={classes.protectLink}>
                Protect Mobile application
              </Link>
              .
            </Typography>
          ) : (
            <Skeleton />
          )}
          <UrgentAlertsBanner count={activeUrgentAlerts.length} />
        </Grid>
      )}

      {!device ? (
        <Grid container={true} size={12} sx={classes.skeletonGrid}>
          <Skeleton width={75} sx={classes.skeletonDateHeader} />
          <Skeleton variant="rounded" width="100%" height={74} />
          <Skeleton variant="rounded" width="100%" height={74} />
        </Grid>
      ) : (
        <>
          {activeAlerts.length === 0 && (
            <Typography variant="body2" sx={classes.fullWidth}>
              Looking great! You are connected and protected.
            </Typography>
          )}
          {sortedAlerts.length > 0 && (
            <>
              <Grid container={true} size={12} sx={classes.alertsListContainer}>
                {currentGroupedAlerts.map((group) => {
                  return (
                    <Grid container={true} key={group.key} sx={classes.alertGroupContainer}>
                      <Grid container={true} sx={classes.alertGroupHeader}>
                        <Typography variant="overline" sx={classes.alertGroupLabel}>
                          {group.label}
                        </Typography>
                      </Grid>

                      {group.alerts.map(({ alert }) => {
                        return <AlertCard alert={alert} variant="device-screen" key={alert.alertId} />
                      })}
                    </Grid>
                  )
                })}
              </Grid>
              {sortedAlerts.length > 5 && (
                <Pagination
                  count={Math.max(1, Math.ceil(sortedAlerts.length / PAGE_SIZE))}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  color="primary"
                  sx={classes.pagination}
                />
              )}
            </>
          )}
        </>
      )}
    </Grid>
  )
}

export default AlertsSection
